import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  buildZipImplementationPrompt,
  buildZipPlanningPrompt,
} from "../utils/devIdePromptGenerators";

export default function DownloadModuleZipDialog({
  open,
  onClose,
  modules = [],
  selectedModule = "ALL",
  selectedProjectId = "",
  activeChange = null,
  onDownload,
}) {
  const moduleOptions = useMemo(
    () => ["ALL", ...(modules || []).filter(Boolean)],
    [modules],
  );
  const [codeModule, setCodeModule] = useState(selectedModule || "ALL");
  const [includeDocs, setIncludeDocs] = useState(true);
  const [docsMode, setDocsMode] = useState("selected");
  const [docModules, setDocModules] = useState(
    selectedModule && selectedModule !== "ALL" ? [selectedModule] : [],
  );
  const [copyStatus, setCopyStatus] = useState("");
  const [manualCopyOpen, setManualCopyOpen] = useState(false);
  const [manualCopyTitle, setManualCopyTitle] = useState("");
  const [manualCopyText, setManualCopyText] = useState("");
  const manualCopyRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCodeModule(selectedModule || "ALL");
      setIncludeDocs(true);
      setDocsMode("selected");
      setDocModules(
        selectedModule && selectedModule !== "ALL" ? [selectedModule] : [],
      );
      setCopyStatus("");
      setManualCopyOpen(false);
      setManualCopyTitle("");
      setManualCopyText("");
    }
  }, [open, selectedModule]);

  useEffect(() => {
    if (manualCopyOpen && manualCopyRef.current) {
      manualCopyRef.current.focus();
      manualCopyRef.current.select();
    }
  }, [manualCopyOpen]);

  const selectedDocModules = includeDocs
    ? docsMode === "all"
      ? ["ALL"]
      : docModules
    : [];

  const promptContext = {
    projectId: selectedProjectId || "",
    codeModule,
    includeDocs,
    docsMode,
    docModules: selectedDocModules,
    changeTitle: activeChange?.title || "",
    changeDescription: activeChange?.description || "",
    docsChanged: includeDocs
      ? "Yes - update existing docs included in the ZIP when needed"
      : "No",
  };

  const openManualCopyDialog = (title, text) => {
    setManualCopyTitle(title || "Copy prompt");
    setManualCopyText(text || "");
    setManualCopyOpen(true);
  };

  const copyTextToClipboard = async (text) => {
    if (!text) {
      return { ok: false, manual: true };
    }

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return { ok: true };
      } catch (error) {
      }
    }

    return { ok: false, manual: true };
  };

  const handleCopyPrompt = async (mode) => {
    const prompt =
      mode === "planning"
        ? buildZipPlanningPrompt(promptContext)
        : buildZipImplementationPrompt(promptContext);

    const title =
      mode === "planning"
        ? "DEV IDE planning prompt"
        : "DEV IDE implementation prompt";

    try {
      const result = await copyTextToClipboard(prompt);
      if (result.ok) {
        setCopyStatus(`${title} copied to clipboard`);
        setTimeout(() => setCopyStatus(""), 2500);
        return;
      }

      openManualCopyDialog(title, prompt);
      setCopyStatus("Manual copy mode opened");
    } catch (error) {
      openManualCopyDialog(title, prompt);
      setCopyStatus("Manual copy mode opened");
    }

    setTimeout(() => setCopyStatus(""), 3000);
  };

  const handleDownload = () => {
    onDownload({
      codeModule,
      includeDocs,
      docModules: docsMode === "all" ? ["ALL"] : docModules,
    });
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Download ZIP with documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {copyStatus ? <Alert severity="success">{copyStatus}</Alert> : null}
            <Typography variant="body2" color="text.secondary">
              Choose the code module scope and, separately, the document module
              scope for markdown documents.
            </Typography>
            <TextField
              select
              label="Code module"
              value={codeModule}
              onChange={(e) => setCodeModule(e.target.value)}
              fullWidth
            >
              {moduleOptions.map((moduleName) => (
                <MenuItem key={moduleName} value={moduleName}>
                  {moduleName === "ALL" ? "All modules" : moduleName}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDocs}
                  onChange={(e) => setIncludeDocs(e.target.checked)}
                />
              }
              label="Include markdown documents"
            />
            {includeDocs ? (
              <>
                <TextField
                  select
                  label="Document scope"
                  value={docsMode}
                  onChange={(e) => setDocsMode(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="selected">Select document modules</MenuItem>
                  <MenuItem value="all">All document modules</MenuItem>
                </TextField>
                {docsMode === "selected" ? (
                  <Autocomplete
                    multiple
                    options={(modules || []).filter(Boolean)}
                    value={docModules}
                    onChange={(_, value) => setDocModules(value)}
                    filterSelectedOptions
                    disableCloseOnSelect={false}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Document modules"
                        helperText="Choose one or more modules whose documents should be included."
                      />
                    )}
                  />
                ) : null}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="outlined" onClick={() => handleCopyPrompt("planning")}>
            Copy planning prompt
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleCopyPrompt("implementation")}
          >
            Copy implementation prompt
          </Button>
          <Button variant="contained" onClick={handleDownload}>
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualCopyOpen}
        onClose={() => setManualCopyOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{manualCopyTitle || "Copy prompt"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Clipboard access was unavailable. Copy the prompt below manually.
            </Typography>
            <TextField
              inputRef={manualCopyRef}
              value={manualCopyText}
              multiline
              minRows={16}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualCopyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
